import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Student-scoped panel — presentation only.
 *
 * Reproduces the HTML mockup's `.panel` chrome exactly:
 *   .panel   { background:surface; border:1px line; border-radius:18px }
 *   .panel-h { padding:16px 20px; border-bottom:1px line; flex; space-between }
 *   .panel-h h3 { font-size:15px; font-weight:700; icon gap 9px }
 *   .panel-b { padding:8px 20px }
 *
 * Prop surface is intentionally ResponsiveCard-compatible (`title`, `subtitle`,
 * `action`, `children`, `sx`) plus an optional header `icon`, so the swap in
 * StudentDashboard is mechanical and reversible. Holds no state and no logic.
 */
const StudentPanel = ({ title, subtitle, action, icon, children, sx = {} }) => (
  <Box
    sx={{
      backgroundColor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '18px',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {(title || subtitle || action) && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: '20px',
          py: '16px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                '& svg': { fontSize: 18 },
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            component="h3"
            sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary' }}
          >
            {title}
          </Typography>
        </Box>

        {action ||
          (subtitle && (
            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', flex: 'none' }}>
              {subtitle}
            </Typography>
          ))}
      </Box>
    )}

    <Box sx={{ px: '20px', py: '8px' }}>{children}</Box>
  </Box>
);

export default StudentPanel;
